import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HttpService } from '@Services/api/httpService';
import { RoutesMap } from '@Router/router.types';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { DocVStatus } from '@Store/slices/ubo/createUboSlice';
import type { AuthUserOnboardingStatus } from '@Store/slices/authOnboarding/authOnboarding.types';

const MAX_POLLING_TIME = 5400000;
const RETRY_DELAY = 2000;
const RETRY_TIMES = 2700;

export interface AuthUserDocVStatusResponse {
  authorizedUserId: string;
  onboardingStatus: AuthUserOnboardingStatus;
  docVStatus: DocVStatus;
  docVUrl?: string;
  qrCode?: string;
}

export const getAuthUserDocVStatus = async (
  customerId: string,
  authorizedUserId: string
): Promise<AuthUserDocVStatusResponse> => {
  const url = `v3/onboarding/user/${customerId}/authorized/user/${authorizedUserId}/docv-status`;
  const { data } = await HttpService.get<AuthUserDocVStatusResponse>(url);
  return data;
};

export interface UseAuthUserDocVStatusPollingParams {
  customerId: string;
  authorizedUserId: string;
}

export const useAuthUserDocVStatusPolling = (params: UseAuthUserDocVStatusPollingParams) => {
  const { customerId, authorizedUserId } = params;
  const setAuthUserDocVData = useBoundStore((state) => state.setAuthUserDocVData);
  const { navigateToSpecificPage } = useNavigationManager();
  const startTimeRef = useRef<number | null>(null);
  const lastStatusRef = useRef<DocVStatus | null>(null);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  return useQuery({
    queryFn: () => getAuthUserDocVStatus(customerId, authorizedUserId),
    queryKey: ['getAuthUserDocVStatus', customerId, authorizedUserId],
    retry: RETRY_TIMES,
    retryDelay: RETRY_DELAY,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      const elapsedTime = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

      if (elapsedTime >= MAX_POLLING_TIME) {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
        return false;
      }

      const { data } = query.state;
      if (data) {
        return handleAuthUserDocVStatus(
          data,
          navigateToSpecificPage,
          setAuthUserDocVData,
          lastStatusRef
        );
      }

      return RETRY_DELAY;
    },
  });
};

export const handleAuthUserDocVStatus = (
  data: AuthUserDocVStatusResponse,
  navigateToSpecificPage: (route: RoutesMap) => void,
  setAuthUserDocVData: (data: { status: DocVStatus; url?: string; qrCode?: string }) => void,
  lastStatusRef: React.MutableRefObject<DocVStatus | null>
): number | false => {
  const { docVStatus, docVUrl, qrCode } = data;
  const hasStatusChanged = lastStatusRef.current !== docVStatus;

  if (hasStatusChanged) {
    lastStatusRef.current = docVStatus;
    setAuthUserDocVData({ status: docVStatus, url: docVUrl, qrCode });
  }

  if (docVStatus === DocVStatus.APPROVED) {
    navigateToSpecificPage(RoutesMap.INVITE_TEAM_MEMBER_FINISHED);
    return false;
  }

  if (docVStatus === DocVStatus.REJECTED) {
    navigateToSpecificPage(RoutesMap.VERIFICATION_FAILED);
    return false;
  }

  if (docVStatus === DocVStatus.WAITING || docVStatus === DocVStatus.MANUAL_REVIEW) {
    return RETRY_DELAY;
  }

  return RETRY_DELAY;
};
